import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * Aurora Badge — small inline pill for status, counts, and labels.
 *
 * Tones use a soft tinted background + matching text, so a row of badges
 * sits quietly inside content without the candy-stripe look of saturated
 * pills. The `solid` variant inverts to a filled chip for cases where the
 * badge IS the headline (e.g. "Applied", "Filled").
 *
 * `size="sm"` is the default. `size="xs"` is for inline tags (e.g. next to
 * a field name).
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium tracking-tight transition-colors',
  {
    variants: {
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        primary: 'bg-primary-soft text-primary-soft-foreground',
        success: 'bg-success-soft text-success-soft-foreground',
        warning: 'bg-warning-soft text-warning-soft-foreground',
        info: 'bg-info-soft text-info-soft-foreground',
        danger: 'bg-destructive-soft text-destructive-soft-foreground',
        outline: 'border border-border bg-card text-muted-foreground',
      },
      variant: {
        soft: '',
        solid: '',
      },
      size: {
        xs: 'px-1.5 py-0.5 text-[10px] leading-none [&_svg]:size-3',
        sm: 'px-2 py-0.5 text-xs [&_svg]:size-3',
        md: 'px-2.5 py-1 text-xs [&_svg]:size-3.5',
      },
    },
    compoundVariants: [
      // Solid overrides — fill with the saturated token instead of the soft.
      {
        variant: 'solid',
        tone: 'primary',
        className: 'bg-primary text-primary-foreground',
      },
      {
        variant: 'solid',
        tone: 'success',
        className: 'bg-success text-success-foreground',
      },
      {
        variant: 'solid',
        tone: 'warning',
        className: 'bg-warning text-warning-foreground',
      },
      {
        variant: 'solid',
        tone: 'info',
        className: 'bg-info text-info-foreground',
      },
      {
        variant: 'solid',
        tone: 'danger',
        className: 'bg-destructive text-destructive-foreground',
      },
      {
        variant: 'solid',
        tone: 'neutral',
        className: 'bg-foreground text-background',
      },
    ],
    defaultVariants: {
      tone: 'neutral',
      variant: 'soft',
      size: 'sm',
    },
  },
)

function Badge({
  className,
  tone,
  variant,
  size,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return (
    <span
      data-slot="badge"
      data-tone={tone ?? 'neutral'}
      data-variant={variant ?? 'soft'}
      className={cn(badgeVariants({ tone, variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants }
